import React, { useState, useContext, useEffect } from 'react';
import { InputActionMeta } from 'react-select';
import CSS from 'csstype';
import '../../App.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// images
import dataStagingIcon from '../../icons/action-data-staging.512.png';
import dataStagingIconDisabled from '../../icons/action-data-staging-bw.512.png';
import launchJupyterIcon from '../../icons/action-launch-jupyter.512.png';
import launchJupyterIconDisabled from '../../icons/action-launch-jupyter-bw.512.png';
import launchCanfarIcon from '../../icons/action-launch-canfar.512.png';
import launchCanfarIconDisabled from '../../icons/action-launch-canfar-bw.512.png';
import binLightIcon from '../../icons/bin-light.256.png';
import binDarkIcon from '../../icons/bin-dark.256.png';

// functions
import { APIPrefix, ContainerHook, siteName } from '../../utils/functions';

// classes
import ToolButton from '../../tools/tool-button';
import { ToolButtonType } from '../../tools/tool-button';
import Scrollbox from '../../tools/scrollbox';
import PleaseWait from '../../tools/please-wait';
import EditableSelect from '../../tools/editable-select';
import { ContextMenuType, ContextMenuItem } from '../menus/context-menu';
import { ContextMenu } from '../menus/context-menu';
import { PopupMessage, MessageType } from '../../tools/popup-message';
import {
  Event,
  EventType,
  EventTarget,
  InitiateDMSearchParams,
  MoveDataToStorage,
  AddCollectionEvent,
  DataCollection,
} from '../../utils/events';

// types
import { FileBrowserPage, RucioLocationsType, Site } from '../../utils/types';

// types specifically for this component.
import { SelectedRowsType } from './search-catalog-types';

// data-management types.
import { handleEvent } from '../../app-actions';

// Select-box custom styles.
import { customStylesDark } from '../../utils/custom-styles-select-box';
import { customStylesLight } from '../../utils/custom-styles-select-box';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

// import state definition and functions.
import {
  rowCheckedHandler,
  setCollections,
  setContextMenuRefreshStageData as setContextMenuRefresh,
  setExistingCollection,
  setExistingCollectionText,
  setSelectedFiles,
  updateSelectedFiles,
} from './search-catalog-slice';
import { loadCollections } from './search-catalog-actions';
import {
  CONTEXT_CONTAINERS,
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_SEARCH_CATALOG_SELECTED_ITEMS as CONTEXT_MENU,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import {
  ctx_buildContextMenu,
  ctx_calculatePosition,
} from '../menus/context-menu-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function SelectedItemsTab(props: { hidden: boolean; launchService: any }) {
  //
  // class-level constants.
  const TABLE_COL: CSS.Properties = {
    padding: '3px 5px 3px 5px',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  };
  const TABLE_COL_SELECTED_DARK: CSS.Properties = {
    background: '#e70068',
  };
  const TABLE_COL_SELECTED_LIGHT: CSS.Properties = {
    background: '#040034',
  };
  const TABLE_COL_UNSELECTED: CSS.Properties = {
    background: 'transparent',
  };

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // GLOBAL
  // --------------------

  const sG_dataManagementTokenObtained: boolean | undefined = useAppSelector(
    (state) => state.global.dataManagementTokenObtained,
  );
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_submittingJob: boolean = useAppSelector((state) => state.global.submittingJob);

  // DATA
  // --------------------

  // rucio locations.
  const sD_rucioLocations: RucioLocationsType[] = useAppSelector(
    (state) => state.data.rucioLocations,
  ).filter((filter) => filter.catalog.indexOf('search-catalog') > -1);

  // LOCAL
  // --------------------

  // maintain the state for the selected rows on the results tabs.
  const sSelectedRows: SelectedRowsType[] = useAppSelector(
    (state) => state.searchCatalog.selectedItems.selectedRows,
  );

  const sSelectedFiles: { namespace: string; filename: string }[] = useAppSelector(
    (state) => state.searchCatalog.selectedItems.selectedFiles,
  );
  const sExistingCollection: { value: number; label: string } = useAppSelector(
    (state) => state.searchCatalog.existingCollection,
  );
  const sExistingCollectionText: string = useAppSelector(
    (state) => state.searchCatalog.existingCollectionText,
  );
  const sCollections: { value: number; label: string }[] = useAppSelector(
    (state) => state.searchCatalog.collections,
  );
  const sContextMenuRefresh: number = useAppSelector(
    (state) => state.searchCatalog.contextMenus.refreshStageData,
  );

  // CONTEXT MENUS
  // --------------------

  const sContextMenuDefinition: ContextMenuType[][] = useAppSelector(
    (state) => state.contextMenu.contextMenuDefinition,
  );

  //	--------------------------------------------------------------------------
  //
  //	C O N T E X T   M E N U   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  const CONTEXT_SYSTEM: CONTEXT_SYSTEMS = CONTEXT_SYSTEMS.SEARCH_CATALOG_SELECTED_ITEMS;

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //	--------------------------------------------------------------------------
  //
  //	H A N D L E   C O N T A I N E R   R E S I Z E
  //
  //	--------------------------------------------------------------------------

  // monitor this container, and if it changes size update the state.
  const dataWindow = ContainerHook({
    container: CONTEXT_CONTAINERS.SEARCH_CATALOG_SELECTED_ITEMS,
  });

  //	--------------------------------------------------------------------------
  //
  //	E V E N T   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	raise a create-collection popup message.
  //
  //	------------------------------------------------------------

  const [sRaiseCreateCollectionMessage, setRaiseCreateCollectionMessage] =
    useState<any>(undefined);

  const setCreateCollectionMessageObject = (newFunction: any) => {
    setRaiseCreateCollectionMessage(() => newFunction);
  }; // setCreateCollectionMessageObject

  //	------------------------------------------------------------
  //
  //	raise an add-to-collection popup message.
  //
  //	------------------------------------------------------------

  const [sRaiseAddToCollectionMessage, setRaiseAddToCollectionMessage] =
    useState<any>(undefined);

  const setAddToCollectionMessageObject = (newFunction: any) => {
    setRaiseAddToCollectionMessage(() => newFunction);
  }; // setAddToCollectionMessageObject

  //	------------------------------------------------------------
  //
  //	raise an existing-collection-name popup message.
  //
  //	------------------------------------------------------------
  const [sExistingCollectionNameError, setExistingCollectionNameError] =
    useState<boolean>(false);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	Get some information and show this file in the data
  //	management tab.
  //
  //	------------------------------------------------------------

  function dataStaging(args: { row: number }) {
    //
    // get the row.
    let row: any = sSelectedRows[args.row];

    // initiate a new data-management search.
    if (row.namespace !== '' && row.filename !== '') {
      //
      // run the data-management search.
      const params: InitiateDMSearchParams = {
        namespace: row.namespace,
        filename: row.filename,
        pageDisplayed: FileBrowserPage.Items,
        showPage: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
        parameters: params,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }
    //
  } // dataStaging

  //	------------------------------------------------------------
  //
  //	retrieves the scroll position of the container that
  //	holds the context menu.
  //
  //	------------------------------------------------------------

  function getContainerScrollPosition() {
    //
    let containerScrollPosition: number = 0.0;

    // get the DIV element with the vertical scroll bars, and find the scroll position.
    const id = 'search-catalog-scrollbox';
    const scrollDiv: HTMLDivElement | null = document.querySelector(`#${id}`);
    if (scrollDiv !== null) {
      containerScrollPosition = scrollDiv.scrollTop;
    }

    // return something.
    return containerScrollPosition;
    //
  } // getContainerScrollPosition

  //	------------------------------------------------------------
  //
  //	Creates a combined list of available and moving sites,
  //	with a boolean property to show if the file is being
  //	moved here.
  //
  //	------------------------------------------------------------

  function getSites(args: { item: SelectedRowsType }) {
    //
    let siteList: {
      site: Site;
      moving: boolean;
    }[] = [];

    const locationIndex: number = sD_rucioLocations.findIndex(
      (item) =>
        item.namespace === args.item.namespace && item.filename === args.item.filename,
    );
    if (locationIndex > -1) {
      //
      const item: RucioLocationsType = sD_rucioLocations[locationIndex];

      // get available sites.
      for (let i: number = 0; i < item.availableSites.length; i++) {
        siteList.push({
          site: item.availableSites[i],
          moving: false,
        });
      }

      // get moving sites.
      for (let i: number = 0; i < item.movingSites.length; i++) {
        siteList.push({
          site: item.movingSites[i],
          moving: true,
        });
      }
      //
    }

    // return something.
    return siteList;
    //
  } // getSites

  //	------------------------------------------------------------
  //
  //	Handler for Select box onInputChange event.
  //
  //	------------------------------------------------------------

  const handleDeleteCollection = async (collectionId: string | number) => {
    //
    try {
      //
      console.log('Deleting collection: ' + collectionId);

      const response = await fetch(APIPrefix() + '/v1/delete_data_collections', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([collectionId]),
        credentials: 'include',
      });

      if (response) {
        //
        if (sExistingCollection?.value === collectionId) {
          dispatch(setExistingCollection({ value: -1, label: '' }));
          dispatch(setExistingCollectionText(''));
        }

        // raise an event to handle the refresh of the collections.
        const project: string | undefined = sG_project?.name ?? undefined;
        const newEvent: Event = {
          eventTarget: EventTarget.GLOBAL,
          eventType: EventType.DATA_COLLECTION_DELETED,
          parameters: project,
        };

        dispatch(
          handleEvent({
            event: newEvent,
          }),
        );
        //
      }
    } catch (error) {
      console.error('Error deleting collection: ', error);
    }
    //
  }; // handleDeleteCollection

  //	------------------------------------------------------------
  //
  //	Launch a service on a site where this data is found.
  //
  //	------------------------------------------------------------

  function launchService(args: { type: string; row: number }) {
    //
    // get the row.
    let row: any = sSelectedRows[args.row];

    // launch the notebook.
    if (row.namespace !== '' && row.filename !== '') {
      props.launchService({
        type: args.type,
        namespace: row.namespace,
        filename: row.filename,
      });
    }
    //
  } // launchService

  //	------------------------------------------------------------
  //
  //	Handler for Select box onChange event.
  //
  //	------------------------------------------------------------

  const onChangeExistingCollectionHandler = (option: any) => {
    //
    // raise an onChange event.
    if (option !== null) {
      dispatch(setExistingCollection(option));
      dispatch(setExistingCollectionText(option.label));
    } else {
      dispatch(setExistingCollection({ value: -1, label: '' }));
      dispatch(setExistingCollectionText(''));
    }
    //
  }; // onChangeExistingCollectionHandler

  //	------------------------------------------------------------
  //
  //	Handler for onClick events (buttons).
  //
  //	------------------------------------------------------------

  const onClickHandlerBtn = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const btn: HTMLButtonElement = event.currentTarget;

    // if we've clicked on a button to stage the data.
    if (btn.name.length > 15)
      if (btn.name.slice(0, 15) === 'btnDataStaging_') {
        //
        // get the rest of the identifier, and convert to numeric.
        let rowID: string = btn.name.slice(15 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // go to data staging.
        if (rowIDNumeric > -1) {
          dataStaging({ row: rowIDNumeric });
        }
        //
      }

    // if we've clicked on a button to launch a notebook.
    if (btn.name.length > 11)
      if (btn.name.slice(0, 11) === 'btnJupyter_') {
        //
        // get the rest of the identifier, and convert to numeric.
        let rowID: string = btn.name.slice(11 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // download the file.
        if (rowIDNumeric > -1) {
          launchService({ type: 'JUPYTERHUB', row: rowIDNumeric });
        }
        //
      }

    // if we've clicked on a button to launch a notebook.
    if (btn.name.length > 10)
      if (btn.name.slice(0, 10) === 'btnCanfar_') {
        //
        // get the rest of the identifier, and convert to numeric.
        let rowID: string = btn.name.slice(10 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // download the file.
        if (rowIDNumeric > -1) {
          launchService({ type: 'CANFAR', row: rowIDNumeric });
        }
        //
      }

    // if we've clicked on a button to unselect a row.
    if (btn.name.length > 12)
      if (btn.name.slice(0, 12) === 'btnUnselect_') {
        //
        // get the rest of the identifier, and convert to numeric.
        let rowID: string = btn.name.slice(12 - btn.name.length);
        let rowIDNumeric: number = -1;
        try {
          rowIDNumeric = Number(rowID);
        } catch (e) {}

        // raise an onChange event.
        if (rowIDNumeric > -1) {
          dispatch(
            rowCheckedHandler({
              rows: [Object.create(sSelectedRows[rowIDNumeric])],
              ticked: [false],
            }),
          );
        }
        //
      }

    // if we've clicked on a button to remove all items.
    if (btn.name === 'unselect-all') {
      //
      // get the list of selected rows from state.
      let rows: SelectedRowsType[] = sSelectedRows.slice();

      // loop over all the rows, adding them to the selected-items list.
      let ticked: boolean[] = [];
      for (let i: number = 0; i < sSelectedRows.length; i++) {
        ticked.push(false);
      }

      // update state.
      dispatch(rowCheckedHandler({ rows: rows, ticked: ticked }));
      //
    }

    // if we've clicked on a button to create a collection.
    if (btn.name === 'create-collection') {
      //
      // build list of dids from the selected items.
      let dids: {
        namespace: string;
        filename: string;
      }[] = [];
      for (let i: number = 0; i < sD_rucioLocations.length; i++)
        if (sD_rucioLocations[i].namespace !== '' && sD_rucioLocations[i].filename !== '')
          dids.push({
            namespace: sD_rucioLocations[i].namespace,
            filename: sD_rucioLocations[i].filename,
          });

      // create a new data collection.
      const dataCollection: DataCollection = {
        project: sG_project,
        dataCollection: {
          id: -1,
          name: sExistingCollectionText,
        },
        dids: dids,
        messageSearchCatalog: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.SEARCH_CATALOG,
        eventType: EventType.CREATE_NEW_DATA_COLLECTION,
        parameters: dataCollection,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }

    // if we've clicked on a button to add to a collection.
    if (btn.name === 'add-to-collection') {
      //
      // build list of dids from the selected items.
      let dids: {
        namespace: string;
        filename: string;
      }[] = [];
      for (let i: number = 0; i < sD_rucioLocations.length; i++)
        if (sD_rucioLocations[i].namespace !== '' && sD_rucioLocations[i].filename !== '')
          dids.push({
            namespace: sD_rucioLocations[i].namespace,
            filename: sD_rucioLocations[i].filename,
          });

      // add to an existing data collection.
      const dataCollection: DataCollection = {
        dataCollection: {
          id: sExistingCollection !== undefined ? sExistingCollection.value : -1,
          name: sExistingCollection !== undefined ? sExistingCollection.label : '',
        },
        project: sG_project,
        dids: dids,
        messageSearchCatalog: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.ADD_TO_EXISTING_DATA_COLLECTION,
        parameters: dataCollection,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }
    //
  }; // onClickHandlerBtn

  //	------------------------------------------------------------
  //
  //	Handler for clicks on div.
  //
  //	------------------------------------------------------------

  const onClickHandlerDiv = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const divElement: HTMLDivElement = event.currentTarget;

    // check for a right-click, and a double click.
    const rightClick: boolean = event.type === 'contextmenu';
    const doubleClick: boolean = event.detail === 2;
    const ctrlClick: boolean = event.ctrlKey === true;
    const shiftClick: boolean = event.shiftKey === true;

    console.log(
      'onClickHandlerDiv fires [selected-items-tab]',
      rightClick,
      doubleClick,
      ctrlClick,
      shiftClick,
    );

    // have we clicked on the data-item area?
    if (divElement.id === 'data-item-container') {
      //
      // check if the user has clicked on an item, or on an empty part of the container.
      let itemClickedOn: boolean = true;
      if (event.target instanceof HTMLDivElement) {
        const targetElement: HTMLDivElement = event.target;
        itemClickedOn = targetElement.id !== 'data-item-container';
      }

      // if the user has not clicked on any items, then disable the context menu
      // options, and deselect the selected item(s).
      if (itemClickedOn === false) {
        //
        // deselect all the context-menu rows, and set all menus to hidden.
        dispatch(
          ctx_setContextRow({
            contextSystem: CONTEXT_SYSTEM,
            row: -1,
          }),
        );
        dispatch(
          ctx_setContextVisible({
            contextSystem: CONTEXT_SYSTEM,
            visible: false,
          }),
        );

        // clear the selection.
        dispatch(setSelectedFiles([]));
        //
      }

      // store items if we rebuild the main menu.
      let mainMenuItems: ContextMenuItem[] | undefined = undefined;

      // if we've right clicked somewhere in the container store the X and Y coordinates, and set the context menu visible.
      if (rightClick === true) {
        //
        // update the text in the main context menu.
        mainMenuItems = ctx_buildContextMenu({
          contextSystem: CONTEXT_SYSTEM,
          contextMenu: CONTEXT_MENU.MAIN,
          parentRowID: -1,
        });

        // check if this position is OK - use the button as a references point.
        ctx_calculatePosition({
          contextSystem: CONTEXT_SYSTEM,
          contextMenu: CONTEXT_MENU.MAIN,
          pos: {
            x: event.clientX,
            y: event.clientY,
          },
        });

        dispatch(
          ctx_setContextVisible({
            contextSystem: CONTEXT_SYSTEM,
            contextMenu: CONTEXT_MENU.MAIN,
            visible: true,
          }),
        );

        dispatch(
          ctx_setContextRow({
            contextSystem: CONTEXT_SYSTEM,
            row: -1,
          }),
        );

        dispatch(setContextMenuRefresh(Date.now()));

        /*
        //
        // get the DIV element with the vertical scroll bars, and find the scroll position.
        const id = 'search-catalog-scrollbox';
        const scrollDiv: HTMLDivElement | null = document.querySelector(`#${id}`);
        let scrollPos: number = 0.0;
        if (scrollDiv !== null) {
          scrollPos = scrollDiv.scrollTop;
        }*/
        //
      } else {
        //
        // hide the context menu.
        dispatch(
          ctx_setContextVisible({
            contextSystem: CONTEXT_SYSTEM,
            visible: false,
          }),
        );
        //
      }

      dispatch(setContextMenuRefresh(Date.now()));
      //
    }

    if (rightClick === true) {
      event.preventDefault();
    }
    //
  }; // onClickHandlerDiv

  //	------------------------------------------------------------
  //
  //	Handle clicks on a TR table element.
  //
  //	------------------------------------------------------------

  function onClickHandlerTr(args: {
    event: React.MouseEvent<HTMLTableSectionElement>;
    item?: SelectedRowsType;
    index?: number;
  }) {
    //
    const rightClick = args.event.type === 'contextmenu';
    const doubleClick = args.event.detail === 2;
    const ctrlClick: boolean = args.event.ctrlKey === true;
    const shiftClick: boolean = args.event.shiftKey === true;

    console.log(
      'onClickHandlerTr fires [selected-items-tab]',
      args.item,
      args.index,
      rightClick,
      doubleClick,
      ctrlClick,
      shiftClick,
    );

    // highlight the item that was clicked on.
    if (
      args.item !== undefined &&
      ((ctrlClick === false && shiftClick === false) || sSelectedFiles.length === 0)
    ) {
      //
      dispatch(
        setSelectedFiles([
          {
            namespace: args.item.namespace,
            filename: args.item.filename,
          },
        ]),
      );
      //
    }

    // if ctrl was pressed, add the highlighted item to the selected list.
    if (args.item !== undefined && ctrlClick === true && shiftClick === false) {
      dispatch(
        updateSelectedFiles({
          addFiles: [
            {
              namespace: args.item.namespace,
              filename: args.item.filename,
            },
          ],
        }),
      );
    }

    if (args.item !== undefined && shiftClick === true && sSelectedFiles.length > 0) {
      //
      // find the index of the selected item.
      const index: number = sSelectedRows.findIndex(
        (item) =>
          item.namespace === args.item?.namespace &&
          item.filename === args.item?.filename,
      );

      // get the last selected item.
      const lastItem: {
        namespace: string;
        filename: string;
      } = sSelectedFiles[sSelectedFiles.length - 1];

      // and find the index.
      const lastItemIndex: number = sSelectedRows.findIndex(
        (item) =>
          item.namespace === lastItem.namespace && item.filename === lastItem.filename,
      );
      if (index > -1 && lastItemIndex > -1) {
        //
        let addFiles: { namespace: string; filename: string }[] = [];

        // loop over all the items in between.
        if (index >= lastItemIndex)
          for (let i = lastItemIndex; i <= index; i++) {
            //
            // add the item to the end of the list.
            addFiles.push(Object.assign({}, sSelectedRows[i]));
            //
          }
        else
          for (let i = lastItemIndex; i >= index; i--) {
            //
            // add the item to the end of the list.
            addFiles.push(Object.assign({}, sSelectedRows[i]));
            //
          }

        // update the state.
        dispatch(updateSelectedFiles({ addFiles: addFiles }));
        //
      }
    }

    if (rightClick === true) {
      args.event.preventDefault();
    }
    //
  } // onClickHandlerTr

  //	------------------------------------------------------------
  //
  //	Handler for Select box onInputChange event.
  //
  //	------------------------------------------------------------

  const onInputChangeSelectHandler = (inputValue: string, meta: InputActionMeta) => {
    //
    if (meta.action === 'input-change') {
      //
      // find this item in the list of options.
      const index: number = sCollections.findIndex(
        (item) => item.label.toUpperCase() === inputValue.toUpperCase(),
      );

      // update the text in the Select box.
      dispatch(setExistingCollectionText(inputValue));

      // update the selected collection if the text exactly matches.
      if (index > -1) {
        dispatch(setExistingCollection(sCollections[index]));
      } else {
        dispatch(setExistingCollection({ value: -1, label: '' }));
      }
    }
    //
  }; // onInputChangeSelectHandler

  //	------------------------------------------------------------
  //
  //	Check if the obs_publisher_did in this row is the selected
  //	one, and return the appropriate style.
  //
  //	------------------------------------------------------------

  function tableColStyle(args: {
    item: any;
    selectedRows: { namespace: string; filename: string }[];
    topLeft?: boolean;
    topRight?: boolean;
    bottomLeft?: boolean;
    bottomRight?: boolean;
  }) {
    //	--------------------------------------------------------------------------
    //
    //	C O N T E X T   V A R I A B L E S   A N D   F U N C T I O N S
    //
    //	--------------------------------------------------------------------------

    //
    let rowIsSelected: boolean = false;
    rowIsSelected =
      args.selectedRows.findIndex(
        (item) =>
          item.namespace === args.item.namespace && item.filename === args.item.filename,
      ) > -1;

    // get border radius value.
    let borderRadius: string = '';
    if (args.topLeft === true) {
      borderRadius = borderRadius + '10px ';
    } else {
      borderRadius = borderRadius + '0px ';
    }
    if (args.topRight === true) {
      borderRadius = borderRadius + '10px ';
    } else {
      borderRadius = borderRadius + '0px ';
    }
    if (args.bottomRight === true) {
      borderRadius = borderRadius + '10px ';
    } else {
      borderRadius = borderRadius + '0px ';
    }
    if (args.bottomLeft === true) {
      borderRadius = borderRadius + '10px';
    } else {
      borderRadius = borderRadius + '0px';
    }

    // get colour values.
    let colourStyle: any = TABLE_COL_UNSELECTED;
    if (rowIsSelected === true) {
      if (sG_darkMode === true) {
        colourStyle = TABLE_COL_SELECTED_DARK;
      } else {
        colourStyle = TABLE_COL_SELECTED_LIGHT;
      }
    }

    // return something.
    return { ...TABLE_COL, ...{ borderRadius: borderRadius }, ...colourStyle };
    //
  } // tableColStyle

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	A HTML component that contains the action buttons for each
  //	row.
  //
  //	------------------------------------------------------------

  function Actions(args: { tableRow: object; index: number; selected?: boolean }) {
    //
    //	--------------------------------------------------------------------------
    //
    //	C O M P O N E N T   C O D E
    //
    //	--------------------------------------------------------------------------

    return (
      <div
        style={{
          display: props.hidden === true ? 'none' : 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '0px 0px 0px 0px',
          width: '150px',
        }}
      >
        <div style={{ flex: '1 1' }} />
        <button
          name={'btnUnselect_' + args.index.toString()}
          type="button"
          title="Unselect item"
          className="action-button"
          onClick={onClickHandlerBtn}
          style={{ height: '30px' }}
        >
          <img
            src={
              sG_darkMode === true
                ? binDarkIcon
                : args.selected === true
                  ? binDarkIcon
                  : binLightIcon
            }
            role="decoration"
            alt=""
            width="30"
            height="30"
          />
        </button>
        <div style={{ flex: '0 0 10px' }} />
        <button
          name={'btnDataStaging_' + args.index.toString()}
          type="button"
          title="Data staging"
          disabled={sG_dataManagementTokenObtained === false}
          className="action-button"
          onClick={onClickHandlerBtn}
          style={{ height: '30px' }}
        >
          <img
            src={
              sG_dataManagementTokenObtained === true
                ? dataStagingIcon
                : dataStagingIconDisabled
            }
            role="decoration"
            alt=""
            width="30"
            height="30"
          />
        </button>
        <div style={{ flex: '0 0 3px' }} />
        <button
          name={'btnJupyter_' + args.index.toString()}
          type="button"
          title="Launch JupyterHUB"
          disabled={sG_dataManagementTokenObtained === false}
          className="action-button"
          onClick={onClickHandlerBtn}
          style={{ height: '30px' }}
        >
          <img
            src={
              sG_dataManagementTokenObtained === true
                ? launchJupyterIcon
                : launchJupyterIconDisabled
            }
            role="decoration"
            alt=""
            width="30"
            height="30"
          />
        </button>
        <div style={{ flex: '0 0 3px' }} />
        <button
          name={'btnCanfar_' + args.index.toString()}
          type="button"
          title="Launch CANFAR"
          disabled={sG_dataManagementTokenObtained === false}
          className="action-button"
          onClick={onClickHandlerBtn}
          style={{ height: '30px' }}
        >
          <img
            src={
              sG_dataManagementTokenObtained === true
                ? launchCanfarIcon
                : launchCanfarIconDisabled
            }
            role="decoration"
            alt=""
            width="30"
            height="30"
          />
        </button>
        <div style={{ flex: '1 1' }} />
      </div>
    );
    //
  } // Actions

  //	------------------------------------------------------------
  //
  //	A HTML component that takes a list of sources and renders
  //	them as a list of table rows.
  //
  //	------------------------------------------------------------

  function TableRows(args: {
    dataItems: SelectedRowsType[];
    selectedRows: { namespace: string; filename: string }[];
    onClickHandlerTr: any;
  }) {
    //
    //	--------------------------------------------------------------------------
    //
    //	F U N C T I O N S
    //
    //	--------------------------------------------------------------------------

    //	-------------------------------------------------
    //
    //	C O M P O N E N T   C O D E
    //
    //	-------------------------------------------------

    try {
      return (
        <>
          {args.dataItems.map((item, index) => (
            <>
              <tr
                className="search-results-table-row, text-colour"
                data-selected={
                  args.selectedRows.findIndex(
                    (selectedItem) =>
                      selectedItem.namespace === item.namespace &&
                      selectedItem.filename === item.filename,
                  ) > -1
                    ? 'T'
                    : 'F'
                }
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  height: '32px',
                }}
                onClick={(evt) =>
                  args.onClickHandlerTr({ event: evt, item: item, index: index })
                }
                onContextMenu={(evt) =>
                  args.onClickHandlerTr({ event: evt, item: item, index: index })
                }
              >
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                    topLeft: true,
                    bottomLeft: true,
                  })}
                  rowSpan={2}
                >
                  {Actions({
                    tableRow: item,
                    index: index,
                    selected:
                      args.selectedRows.findIndex(
                        (selectedItem) =>
                          selectedItem.namespace === item.namespace &&
                          selectedItem.filename === item.filename,
                      ) > -1,
                  })}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                  })}
                >
                  {item.namespace}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                  })}
                >
                  {item.filename}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                  })}
                >
                  {item.targetName}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                  })}
                >
                  {item.obsID}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                  })}
                >
                  {item.obsPublisherDid}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                  })}
                >
                  {item.dataProductType}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                  })}
                >
                  {item.facilityName}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                  })}
                >
                  {item.sRA}
                </td>
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                    topRight: true,
                  })}
                >
                  {item.sDec}
                </td>
              </tr>
              <tr
                className="search-results-table-row, text-colour"
                data-narrow="T"
                data-selected={
                  args.selectedRows.findIndex(
                    (selectedItem) =>
                      selectedItem.namespace === item.namespace &&
                      selectedItem.filename === item.filename,
                  ) > -1
                    ? 'T'
                    : 'F'
                }
                data-muted="T"
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  height: '26px',
                }}
                onClick={(evt) =>
                  args.onClickHandlerTr({ event: evt, item: item, index: index })
                }
                onContextMenu={(evt) =>
                  args.onClickHandlerTr({ event: evt, item: item, index: index })
                }
              >
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: args.selectedRows,
                    bottomRight: true,
                  })}
                  colSpan={9}
                >
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <div style={{ flex: '0 0 auto' }}>{'Located at:'}</div>
                    <div style={{ flex: '0 0 10px' }} />
                    <div
                      style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'row' }}
                    >
                      {getSites({ item: item }).map((site, siteIndex) => (
                        <div>
                          {(siteIndex === 0 ? '' : ', ') +
                            siteName({ site: site.site }) +
                            (site.moving === true ? ' (in transit)' : '')}
                        </div>
                      ))}
                    </div>
                  </div>
                </td>
              </tr>
              <tr
                className="search-results-table-row"
                data-separator="T"
                style={{
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={(evt) =>
                  args.onClickHandlerTr({ event: evt, item: item, index: index })
                }
                onContextMenu={(evt) =>
                  args.onClickHandlerTr({ event: evt, item: item, index: index })
                }
              >
                <td
                  style={tableColStyle({
                    item: item,
                    selectedRows: [],
                  })}
                  colSpan={9}
                ></td>
              </tr>
            </>
          ))}
        </>
      );
    } catch (e) {
      return <></>;
    }
  } // TableRows

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: props.hidden === true ? 'none' : 'flex',
        margin: '10px 0px 0px 0px',
        flexDirection: 'column',
      }}
    >
      <Scrollbox
        id="search-catalog-scrollbox"
        direction="vertical"
        /*width="100%"*/
        flex="1 1"
        flexDirection="column"
        align="normal"
      >
        <div
          ref={dataWindow}
          id="data-item-container"
          className="text-colour"
          style={{ overflowX: 'auto', height: '100%', flex: '1 1', position: 'relative' }}
          onClick={onClickHandlerDiv}
          onContextMenu={onClickHandlerDiv}
        >
          <table className="search-results-table">
            <colgroup>
              <col key="Actions_col" />
              <col key="namespace" />;
              <col key="filename" />;
              <col key="obs_publisher_did" />;
              <col key="target_name" />;
              <col key="obs_id" />;
              <col key="dataproduct_type" />;
              <col key="facility_name" />;
              <col key="s_ra" />;
              <col key="s_dec" />;
            </colgroup>
            <thead>
              <tr className="search-results-table-row-header">
                <th style={TABLE_COL}>Actions</th>
                <th style={TABLE_COL} data-hidden="true" key="namespace">
                  Namespace
                </th>
                <th style={TABLE_COL} data-hidden="true" key="filename">
                  Filename
                </th>
                <th style={TABLE_COL} data-hidden="true" key="target_name">
                  target_name
                </th>
                <th style={TABLE_COL} data-hidden="true" key="obs_id">
                  obs_id
                </th>
                <th style={TABLE_COL} data-hidden="true" key="obs_publisher_did">
                  obs_publisher_did
                </th>
                <th style={TABLE_COL} data-hidden="true" key="dataproduct_type">
                  dataproduct_type
                </th>
                <th style={TABLE_COL} data-hidden="true" key="facility_name">
                  facility_name
                </th>
                <th style={TABLE_COL} data-hidden="true" key="s_ra">
                  s_ra
                </th>
                <th style={TABLE_COL} data-hidden="true" key="s_dec">
                  s_dec
                </th>
              </tr>
            </thead>
            <tbody>
              {TableRows({
                dataItems: sSelectedRows,
                selectedRows: sSelectedFiles,
                onClickHandlerTr: onClickHandlerTr,
              })}
            </tbody>
          </table>

          {/* add the context menus */}
          {/*<ContextMenu key={sContextMenuRefresh.toString()} system={CONTEXT_SYSTEM} />*/}
        </div>
      </Scrollbox>
      <div style={{ flex: '0 0 10px' }} />
      <Scrollbox
        direction="horizontal"
        flex="0 0 auto"
        width="auto"
        flexDirection="column"
      >
        <div style={{ flex: '0 0 10px' }} />
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div style={{ flex: '0 0 10px' }} />
          <div style={{ flex: '0 0 auto' }}>
            <ToolButton
              name="unselect-all"
              text="Unselect all"
              tooltip="Unselect all selected items"
              disabled={sSelectedRows.length === 0}
              onClick={onClickHandlerBtn}
              type={ToolButtonType.SECONDARY}
            />
          </div>
          <div style={{ flex: '0 0 15px' }} />
          <div
            style={{
              border: '1pt solid #555',
              borderRadius: '6px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flex: '1 1',
              padding: '10px',
              position: 'relative',
            }}
          >
            <div
              className="text-colour panel-gradient-background"
              style={{
                position: 'absolute',
                top: '-12px',
                width: 'auto',
                padding: '0px 5px 0px 5px',
              }}
            >
              {t('Data collections')}
            </div>
            <div style={{ flex: '0 0 5px' }} />
            <label
              id="search-catalog-tab-collection-label"
              className="text-colour"
              style={{
                marginRight: '10px',
              }}
            >
              Collection:
            </label>
            <div
              id="existingCollectionNameDiv"
              style={{
                alignItems: 'center',
                flex: '0 0 250px',
                border:
                  sExistingCollectionNameError === true
                    ? '2pt solid red'
                    : '2pt solid transparent',
              }}
            >
              <EditableSelect
                aria-labelledby="search-catalog-tab-collection-label"
                gatewayTheme={sG_darkMode === true ? 'dark' : 'light'}
                styles={sG_darkMode === true ? customStylesDark : customStylesLight}
                isDisabled={sD_rucioLocations.length === 0}
                options={sCollections}
                placeholder={t('Collection')}
                value={sExistingCollection}
                onChange={onChangeExistingCollectionHandler}
                inputValue={sExistingCollectionText}
                onInputChange={onInputChangeSelectHandler}
                onDelete={handleDeleteCollection}
                controlShouldRenderValue={false}
              />
            </div>

            <div
              id="createCollectionDiv"
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'row',
                position: 'relative',
                margin: '0px 0px 0px 10px',
              }}
            >
              <ToolButton
                key={t('Create')}
                name="create-collection"
                onClick={onClickHandlerBtn}
                tooltip="Create a new data collection"
                text={t('Create')}
                type={ToolButtonType.SECONDARY}
                disabled={
                  sD_rucioLocations.length === 0 ||
                  sExistingCollectionText === '' ||
                  sExistingCollection.value > -1
                }
              />
            </div>

            <div
              id="addToCollectionDiv"
              style={{
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'row',
                margin: '0px 0px 0px 10px',
              }}
            >
              <ToolButton
                key={t('Add to')}
                name="add-to-collection"
                onClick={onClickHandlerBtn}
                tooltip="Add to an existing data collection"
                text={t('Add to')}
                type={ToolButtonType.SECONDARY}
                disabled={
                  sD_rucioLocations.length === 0 || sExistingCollection.value === -1
                }
              />
            </div>
          </div>

          <div style={{ flex: '0 0 10px' }} />
          <div
            style={{
              display: sG_submittingJob === true ? 'flex' : 'none',
              flex: '0 0 150px',
              alignItems: 'center',
              textAlign: 'center',
              margin: '0px 10px 0px 10px',
            }}
          >
            <PleaseWait text="Submitting job..." visible={true} />
          </div>
          {/*<div style={{ flex: '0 0 10px' }} />*/}
        </div>
        <div style={{ flex: '0 0 10px' }} />
      </Scrollbox>
    </div>
  );
  //
} // SelectedItemsTab
